import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchBusMessages, type BusMessage } from '../api/bus';
import './BusPage.css';

/**
 * The message bus, as the runtime recorded it.
 *
 * Ported from the predecessor's `BusInspector`. Two things changed
 * deliberately:
 *
 * **The filter options say where they came from.** They are the distinct
 * senders, recipients and types *in the loaded window*, not everything
 * the bus has ever carried — there is no endpoint that enumerates those.
 * The predecessor built the same dropdowns from the same window and
 * presented them as the whole set, so a sender absent from the last
 * hundred messages could not be selected and the list looked complete.
 * The window size is stated next to them.
 *
 * **A payload is rendered, never interpreted.** `payload` is whatever the
 * sender put there and nothing validates it on the way out, so this page
 * pretty-prints it and does not reach for a field.
 */

const REFRESH_MS = 10_000;
const WINDOW = 100;

function timestamp(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * A payload as text.
 *
 * Handles the double-encoded case — a JSON string containing JSON —
 * because the bus carries both, depending on the sender.
 */
function renderPayload(raw: unknown): string {
  if (raw === null || raw === undefined) return '(empty)';
  if (typeof raw === 'string') {
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  }
  return JSON.stringify(raw, null, 2);
}

export function BusPage() {
  const [messages, setMessages] = useState<BusMessage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [sender, setSender] = useState('');
  const [recipient, setRecipient] = useState('');
  const [messageType, setMessageType] = useState('');

  const load = useCallback(async () => {
    try {
      const rows = await fetchBusMessages({
        limit: WINDOW,
        sender: sender || undefined,
        recipient: recipient || undefined,
        message_type: messageType || undefined,
      });
      setMessages(rows);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [sender, recipient, messageType]);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const { senders, recipients, types } = useMemo(() => {
    const rows = messages ?? [];
    return {
      senders: [...new Set(rows.map((m) => m.sender))].sort(),
      recipients: [...new Set(rows.map((m) => m.recipient))].sort(),
      types: [...new Set(rows.map((m) => m.message_type))].sort(),
    };
  }, [messages]);

  return (
    <div className="bus-page">
      <header className="bus-page__header">
        <div>
          <h1 className="bus-page__title">Bus</h1>
          <p className="bus-page__note">
            Inter-agent messages, newest first · refreshes every{' '}
            {REFRESH_MS / 1000}s
          </p>
        </div>
        {messages !== null && (
          <span className="bus-dim">
            {messages.length} of the last {WINDOW}
          </span>
        )}
      </header>

      {error && <div className="bus-error">{error}</div>}

      <div className="bus-filters">
        <Filter
          label="sender"
          value={sender}
          options={senders}
          onChange={setSender}
        />
        <Filter
          label="recipient"
          value={recipient}
          options={recipients}
          onChange={setRecipient}
        />
        <Filter
          label="type"
          value={messageType}
          options={types}
          onChange={setMessageType}
        />
        <span className="bus-filters__caveat bus-dim">
          options are the values present in the loaded {WINDOW}, not every
          value the bus has carried
        </span>
      </div>

      {messages === null ? (
        <p className="bus-dim">Loading…</p>
      ) : messages.length === 0 ? (
        <p className="bus-dim">
          No messages match. The bus is empty on an instance whose agents
          have not yet spoken to one another.
        </p>
      ) : (
        <ul className="bus-list">
          {messages.map((m) => {
            const open = expanded === m.message_id;
            return (
              <li
                key={m.message_id}
                className={`bus-row${open ? ' is-open' : ''}`}
              >
                <button
                  type="button"
                  className="bus-row__head"
                  onClick={() => setExpanded(open ? null : m.message_id)}
                  aria-expanded={open}
                >
                  <span
                    className={`bus-sev bus-sev--${m.severity.toLowerCase()}`}
                  >
                    {m.severity}
                  </span>
                  <span className="bus-row__type">{m.message_type}</span>
                  <span className="bus-row__route">
                    {m.sender}
                    <span className="bus-dim"> → </span>
                    {m.recipient}
                  </span>
                  <span className="bus-row__time bus-dim">
                    {timestamp(m.created_at_ms)}
                  </span>
                </button>
                {open && (
                  <div className="bus-row__body">
                    <dl className="bus-meta">
                      <dt>message</dt>
                      <dd>
                        <code>{m.message_id}</code>
                      </dd>
                      {m.correlation_id && (
                        <>
                          <dt>correlation</dt>
                          <dd>
                            <code>{m.correlation_id}</code>
                          </dd>
                        </>
                      )}
                      {m.status && (
                        <>
                          <dt>status</dt>
                          <dd>
                            {m.status}
                            {typeof m.attempts === 'number' &&
                              ` · ${m.attempts} attempt${m.attempts === 1 ? '' : 's'}`}
                          </dd>
                        </>
                      )}
                      {m.claimed_at_ms && (
                        <>
                          <dt>claimed</dt>
                          <dd>{timestamp(m.claimed_at_ms)}</dd>
                        </>
                      )}
                      {m.processed_at_ms && (
                        <>
                          <dt>processed</dt>
                          <dd>{timestamp(m.processed_at_ms)}</dd>
                        </>
                      )}
                    </dl>
                    {m.error && <p className="bus-error">{m.error}</p>}
                    <pre className="bus-payload">
                      {renderPayload(m.payload)}
                    </pre>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Filter({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (next: string) => void;
}) {
  return (
    <label className="bus-filter">
      <span className="bus-filter__label">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">any</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}
